import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogHeader } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ArrowLeft, List, Columns } from 'lucide-react';
import { Button } from './ui/button';
import type { WeldPass } from './PassViewTable';
import WeldSegmentViewTable, { type WeldSegmentRecord } from './WeldSegmentViewTable';
import WeldSegmentDetailModal from './WeldSegmentDetailModal';
import { useWeldSegmentView } from '../redux/WeldData/hooks/useWeldData';
import { formatDuration, formatDateTime } from '../lib/formatUtils';
import { convertWeldDataValue, getParameterUnitSuffix } from '../utils/unitUtils';
import { ColumnSelectorModal, type ColumnDefinition } from './ui/ColumnSelectorModal';
import { createUpdateUserTableLayout, getAllUserTableLayouts } from '../redux/UserTableLayout/actions';
import { HEAT_INPUT_DISPLAY_NAME } from '../utils/weldParameterLabels';
import {
  filterColumnsForManualWelding,
  formatManualWeldingBugTypeLabel,
} from '../utils/manualWeldingUtils';

interface WeldSegmentViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  passData: WeldPass;
  weldData?: any;
  statusCalculationMethod?: string;
}

const DEFAULT_PARAMETER_KEYS = [
  'current',
  'voltage',
  'travel-speed',
  'heat',
  'wire-speed',
];

const WELD_SEGMENT_VIEW_COLUMNS: ColumnDefinition[] = [
  { key: 'sl-no', label: 'Sl.no', category: 'Basic Info' },
  // Status column temporarily disabled for weld segment view — re-enable when needed
  // { key: 'status', label: 'Status', category: 'Basic Info' },
  { key: 'station', label: 'Station', category: 'Basic Info' },
  { key: 'actions', label: 'Actions', category: 'Basic Info' },
  { key: 'event', label: 'Event', category: 'Basic Info' },
  { key: 'bug-type', label: 'Bug Type', category: 'Basic Info' },
  { key: 'time', label: 'Time', category: 'Basic Info' },
  { key: 'arc-time', label: 'Arc Time', category: 'Basic Info' },
  { key: 'pass', label: 'Pass', category: 'Basic Info' },
  { key: 'torch', label: 'Torch', category: 'Basic Info' },
  {
    key: 'current',
    label: 'Current',
    category: 'Parameters',
    subColumns: [
      { key: 'current-min', label: 'Min' },
      { key: 'current-avg', label: 'Avg' },
      { key: 'current-max', label: 'Max' },
    ],
  },
  {
    key: 'voltage',
    label: 'Voltage',
    category: 'Parameters',
    subColumns: [
      { key: 'voltage-min', label: 'Min' },
      { key: 'voltage-avg', label: 'Avg' },
      { key: 'voltage-max', label: 'Max' },
    ],
  },
  {
    key: 'travel-speed',
    label: 'Travel Speed',
    category: 'Parameters',
    subColumns: [
      { key: 'travel-speed-min', label: 'Min' },
      { key: 'travel-speed-avg', label: 'Avg' },
      { key: 'travel-speed-max', label: 'Max' },
    ],
  },
  {
    key: 'wire-speed',
    label: 'Wire Speed',
    category: 'Parameters',
    subColumns: [
      { key: 'wire-speed-min', label: 'Min' },
      { key: 'wire-speed-avg', label: 'Avg' },
      { key: 'wire-speed-max', label: 'Max' },
    ],
  },
  {
    key: 'heat',
    label: HEAT_INPUT_DISPLAY_NAME,
    category: 'Parameters',
    subColumns: [
      { key: 'heat-min', label: 'Min' },
      { key: 'heat-avg', label: 'Avg' },
      { key: 'heat-max', label: 'Max' },
    ],
  },
];

const TABLE_NAME = 'weldSegmentView';

const getDefaultColumnKeys = (columns: ColumnDefinition[]): string[] => {
  const keys: string[] = [];
  columns.forEach((col) => {
    if (col.category === 'Parameters') {
      if (DEFAULT_PARAMETER_KEYS.includes(col.key)) {
        keys.push(col.key);
        col.subColumns?.forEach((sub) => {
          if (sub.key.endsWith('-avg')) {
            keys.push(sub.key);
          }
        });
      }
    } else {
      keys.push(col.key);
      col.subColumns?.forEach((sub) => {
        if (sub.key.endsWith('-avg')) {
          keys.push(sub.key);
        }
      });
    }
  });
  return keys;
};

const colorPalette = [
  'bg-green-200', 'bg-blue-100', 'bg-green-100', 'bg-red-100',
  'bg-cyan-100', 'bg-teal-100', 'bg-indigo-100', 'bg-lime-100',
  'bg-pink-100', 'bg-rose-100', 'bg-emerald-100', 'bg-violet-100',
];

const WeldSegmentViewModal: React.FC<WeldSegmentViewModalProps> = ({
  isOpen,
  onClose,
  passData,
  weldData,
  statusCalculationMethod = 'Instantaneous',
}) => {
  const dispatch = useDispatch();
  const [selectedSegment, setSelectedSegment] = useState<WeldSegmentRecord | null>(null);
  const [activeTab, setActiveTab] = useState('table');
  const [showAllInfo, setShowAllInfo] = useState(false);
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => getDefaultColumnKeys(WELD_SEGMENT_VIEW_COLUMNS));
  const [defaultColumnsFromAPI, setDefaultColumnsFromAPI] = useState<string[] | null>(null);
  const layoutIdRef = useRef<number | null>(null);

  const userTableLayouts = useSelector((state: any) => state.userTableLayout?.layouts || []);
  const createUpdateSuccess = useSelector((state: any) => state.userTableLayout?.currentLayout);

  const {
    weldSegmentView,
    weldSegmentViewLoading,
    weldSegmentViewError,
    loadWeldSegmentView,
    clearWeldSegmentViewData,
  } = useWeldSegmentView();

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllUserTableLayouts({ table_name: TABLE_NAME }));
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (userTableLayouts.length > 0) {
      const layout = userTableLayouts.find((item: any) => item.table_name === TABLE_NAME);
      if (layout) {
        layoutIdRef.current = layout.user_table_layout_id;
        if (layout.layout?.columns && Array.isArray(layout.layout.columns)) {
          const columns = filterColumnsForManualWelding(layout.layout.columns).filter(
            (key: string) => key !== 'status'
          );
          setVisibleColumns(columns);
          setDefaultColumnsFromAPI(columns);
        }
      }
    }
  }, [userTableLayouts]);

  useEffect(() => {
    if (createUpdateSuccess && createUpdateSuccess.table_name === TABLE_NAME) {
      layoutIdRef.current = createUpdateSuccess.user_table_layout_id;
    }
  }, [createUpdateSuccess]);

  const handleColumnsSave = useCallback((selectedColumns: string[]) => {
    setVisibleColumns(selectedColumns);
    const payload: any = {
      table_name: TABLE_NAME,
      layout: { columns: selectedColumns },
    };
    if (layoutIdRef.current) {
      payload.user_table_layout_id = layoutIdRef.current;
    }
    dispatch(createUpdateUserTableLayout(payload));
  }, [dispatch]);

  useEffect(() => {
    if (isOpen && passData?.welduid_s_event_torch) {
      const torchType = passData.torch && passData.torch !== '-' ? passData.torch : 'Lead';
      loadWeldSegmentView(passData.welduid_s_event_torch, 'Pass', torchType);
    }

    return () => {
      if (!isOpen) {
        clearWeldSegmentViewData();
        setSelectedSegment(null);
      }
    };
  }, [isOpen, passData, loadWeldSegmentView, clearWeldSegmentViewData]);

  const segments = useMemo(
    () => weldSegmentView?.data?.data ?? [],
    [weldSegmentView]
  );

  const getUnitLabel = (parameter: string): string => getParameterUnitSuffix(parameter);

  const convertDiameterValue = (value: any): string => {
    if (value === undefined || value === null || value === '') return '-';
    const numericValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numericValue)) return String(value);
    return convertWeldDataValue(numericValue).toFixed(2);
  };

  const details = useMemo(() => [
    { label: 'Job Number', value: passData.jobNumber || weldData?.wpsNumber || '-' },
    { label: 'Pass', value: passData.name || '-' },
    { label: 'Station', value: passData.stationNumber ?? '-' },
    { label: 'Bug Type', value: formatManualWeldingBugTypeLabel(passData.bugType) },
    { label: 'Torch', value: passData.torch || '-' },
    { label: 'Bug Number', value: passData.bugNumber || '-' },
    { label: `Pipe Diameter (${getUnitLabel('pipe_diameter')})`, value: convertDiameterValue(passData.pipeDiameter) },
    { label: 'Weld Start Time', value: passData.startTime ? formatDateTime(passData.startTime) : '-' },
    { label: 'Weld End Time', value: passData.endTime ? formatDateTime(passData.endTime) : '-' },
    { label: 'Weld Time', value: passData.duration != null ? formatDuration(passData.duration) : '-' },
    { label: 'Welder Id', value: passData.welderId || '-' },
    { label: 'Segments', value: String(segments.length) },
  ], [passData, weldData, segments.length, getUnitLabel]);

  const infoItems = details.map((item, index) => ({
    ...item,
    bgColor: colorPalette[index % colorPalette.length],
  }));

  const visibleItems = showAllInfo ? infoItems : infoItems.slice(0, 7);

  const handleSegmentClick = useCallback((segment: WeldSegmentRecord) => {
    setSelectedSegment(segment);
  }, []);

  const handleDetailClose = useCallback(() => {
    setSelectedSegment(null);
  }, []);

  if (selectedSegment) {
    return (
      <WeldSegmentDetailModal
        isOpen={isOpen}
        onClose={handleDetailClose}
        passData={passData}
        segmentData={selectedSegment}
        weldData={weldData}
        statusCalculationMethod={statusCalculationMethod}
      />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0 overflow-hidden">
        <div className="flex flex-col h-full font-sans overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between bg-white flex-shrink-0">
            <div className="flex items-center gap-4">
              <Button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" variant="outline">
                <ArrowLeft className="h-5 w-5 text-gray-500" />
              </Button>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-bold">
                  {weldData?.weldNumber || passData.bugNumber || passData.id || '-'}
                </h1>
                <div className="text-xs sm:text-sm text-gray-600 mt-1 space-y-1">
                  <div className="flex flex-wrap gap-2">
                    {visibleItems.map((item, index) => (
                      <span key={index} className={`${item.bgColor} px-2 py-1 rounded text-xs`}>
                        {item.label}: {item.value}
                      </span>
                    ))}
                    {infoItems.length > 7 && (
                      <button
                        onClick={() => setShowAllInfo(!showAllInfo)}
                        className="bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                      >
                        {showAllInfo ? 'Show Less' : `Show More (+${infoItems.length - 7})`}
                        <span className="text-xs">{showAllInfo ? '▲' : '▼'}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 flex flex-col min-h-0">
            {weldSegmentViewLoading ? (
              <div className="flex-1 flex items-center justify-center bg-white">
                <div className="flex flex-col items-center space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EB1C37]" />
                  <p className="text-gray-600 text-lg font-medium">Loading weld segment data...</p>
                  <p className="text-gray-400 text-sm">Please wait while we fetch the data</p>
                </div>
              </div>
            ) : weldSegmentViewError ? (
              <div className="flex-1 flex items-center justify-center bg-white">
                <div className="flex flex-col items-center space-y-4 max-w-md text-center">
                  <div className="text-red-500 text-5xl">⚠️</div>
                  <p className="text-red-600 text-lg font-medium">Error loading weld segment data</p>
                  <p className="text-gray-500 text-sm">{weldSegmentViewError}</p>
                  <Button onClick={onClose} variant="outline">Close</Button>
                </div>
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col">
                <div className="sticky top-0 bg-white rounded-t-lg z-10 border-b border-gray-200 flex-shrink-0">
                  <div className="flex items-center justify-between px-1 md:px-4 py-2">
                    <TabsList className="h-auto flex-1 justify-start gap-2 md:gap-4 lg:gap-8 border-b bg-transparent py-2 flex-wrap">
                      <TabsTrigger value="table" className="data-[state=active]:border-[#EB1C37] data-[state=active]:text-[#EB1C37] relative px-1 pb-3 pt-2 text-sm md:text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px] whitespace-nowrap">
                        <List className="inline-block mr-1 h-4 w-4 align-text-bottom" /> weld Segments
                      </TabsTrigger>
                    </TabsList>
                    {activeTab === 'table' && (
                      <div className="flex gap-2 px-2">
                        <Button onClick={() => setIsColumnModalOpen(true)} className="flex items-center gap-2" title="Configure columns">
                          <Columns className="h-4 w-4" />
                          <span className="hidden sm:inline">Columns</span>
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <TabsContent value="table" className="flex-1 min-h-0 overflow-hidden">
                  <div className="h-full px-4">
                    <WeldSegmentViewTable
                      segments={segments}
                      loading={false}
                      onSegmentClick={handleSegmentClick}
                      visibleColumns={visibleColumns}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </div>
        </div>
      </DialogContent>

      <ColumnSelectorModal
        isOpen={isColumnModalOpen}
        onClose={() => setIsColumnModalOpen(false)}
        onSave={handleColumnsSave}
        columns={WELD_SEGMENT_VIEW_COLUMNS}
        selectedColumns={visibleColumns}
        title="Select Columns - Weld Segment View"
        defaultColumns={defaultColumnsFromAPI || getDefaultColumnKeys(WELD_SEGMENT_VIEW_COLUMNS)}
      />
    </Dialog>
  );
};

export default WeldSegmentViewModal;
